import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Container,
  Box,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Checkbox,
  Paper,
  TableContainer,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import Intro from "../../../components/atoms/Intro/Intro.component";
import { authHeader } from "../../../helpers";
import { apiConstants } from "../../../constants";

/**
 * Helper function to upload images.
 * Adapts to your existing image upload logic.
 */
async function uploadImage(file) {
  const formData = new FormData();
  formData.append("image", file);
  const response = await fetch(`${apiConstants.API_URL}/images/shopify`, {
    method: "POST",
    headers: {
      Authorization: authHeader().Authorization,
    },
    body: formData,
  });
  const data = await response.json();
  const resData = {};
  if (data?.data?.path) {
    resData.path = data.data.path;
    resData.url = data.data.url;
  }
  return resData;
}

const ShopifySingleView = () => {
  const { id, collectionId } = useParams();
  const [storeDetails, setStoreDetails] = useState(null);
  const [storeItems, setStoreItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedItems, setSelectedItems] = useState([]);
  const [syncWizardOpen, setSyncWizardOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [syncData, setSyncData] = useState([]);
  const [availableSizes, setAvailableSizes] = useState([]);
  const [isSubmittingSync, setIsSubmittingSync] = useState(false);

  // Fetch available sizes
  useEffect(() => {
    fetch(`${apiConstants.API_URL}/sizes`, {
      method: "GET",
      headers: authHeader(),
    })
      .then((response) => response.json())
      .then((data) => setAvailableSizes(data?.data || []))
      .catch((error) => console.error("Error fetching sizes:", error));
  }, []);

  // Fetch store details
  useEffect(() => {
    fetch(`${apiConstants.API_URL}/shopify_club/${collectionId}`, {
      method: "GET",
      headers: authHeader(),
    })
      .then((response) => response.json())
      .then((data) => setStoreDetails(data?.data || {}))
      .catch((error) => console.error("Error fetching store details:", error));
  }, [collectionId]);

  // Fetch store items
  useEffect(() => {
    fetch(`${apiConstants.API_URL}/shopify_clubs/store_items/${collectionId}`, {
      method: "GET",
      headers: authHeader(),
    })
      .then((response) => response.json())
      .then((data) => {
        setStoreItems(data?.data || []);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching store items:", error);
        setIsLoading(false);
      });
  }, [collectionId]);

  const handleSelect = (itemId) => {
    setSelectedItems((prev) =>
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleOpenSyncWizard = () => {
    if (!selectedItems.length) {
      alert("Please select at least one product to sync.");
      return;
    }
    const initialSyncData = storeItems
      .filter((item) => selectedItems.includes(item.id))
      .map((item) => ({
        id: item.id,
        style: item.production_order?.style || "-",
        gender: item.gender || "",
        unit_price: item.unit_price || 0,
        cost: item.unit_price || 0,
        shippingFee: 0,
        personalizationFee: 0,
        rebates: 0,
        wholesaleTransactionalFee: 0,
        gst: 0,
        finalShopifyPrice: 0,
        sizes: [],
        description: item.description || "",
        image1: item.image1_url || "",
        image2: item.image2_url || "",
        image3: item.image3_url || "",
        image1_path: item.image1_path || "",
        image2_path: item.image2_path || "",
        image3_path: item.image3_path || "",
        enableRebates: false,          // new flag
      }));
    setSyncData(initialSyncData);
    setActiveStep(0);
    setSyncWizardOpen(true);
  };

  const updateCurrentItem = (updates) => {
    setSyncData((prev) =>
      prev.map((item, idx) =>
        idx === activeStep ? { ...item, ...updates } : item
      )
    );
  };

  const handleImageUpload = async (file, fieldName) => {
    if (!file) return;
    try {
      const resData = await uploadImage(file);
      updateCurrentItem({
        [fieldName]: resData.url,
        [`${fieldName}_path`]: resData.path,
      });
    } catch (error) {
      console.error("Image upload error:", error);
      alert("Error uploading image.");
    }
  };

  const totalSteps = syncData.length;

  const handleNext = () => {
    if (activeStep < totalSteps - 1) {
      setActiveStep((prev) => prev + 1);
    } else {
      handleSubmitSync();
    }
  };

  const handleBack = () => activeStep > 0 && setActiveStep((prev) => prev - 1);

  const handleSubmitSync = () => {
    setIsSubmittingSync(true);
    fetch(
      `${apiConstants.API_URL}/shopify_clubs/store_items/sync/${collectionId}`,
      {
        method: "POST",
        headers: { ...authHeader(), "Content-Type": "application/json" },
        body: JSON.stringify({ products: syncData }),
      }
    )
      .then((response) => response.json())
      .then(() => {
        setSyncWizardOpen(false);
        setSelectedItems([]);
      })
      .catch((error) => console.error("Error syncing products:", error))
      .finally(() => setIsSubmittingSync(false));
  };

  const renderWizardContent = () => {
    const currentItem = syncData[activeStep] || {};

    const handleFeeChange = (field, event) => {
      const value = parseFloat(event.target.value) || 0;
      const unit = parseFloat(currentItem.unit_price) || 0;
      const ship = parseFloat(currentItem.shippingFee) || 0;
      const pers = parseFloat(currentItem.personalizationFee) || 0;
      const reb = parseFloat(currentItem.rebates) || 0;
      const wholesaleFee = parseFloat((unit * 0.06).toFixed(2));
      const subtotal = unit + ship + pers + wholesaleFee + reb;
      const cost = unit + ship + pers + wholesaleFee;
      const gst = parseFloat((subtotal * 0.1).toFixed(2));
      const finalPrice = parseFloat((subtotal + gst).toFixed(2));
      updateCurrentItem({
        [field]: value,
        wholesaleTransactionalFee: wholesaleFee,
        cost,
        gst,
        finalShopifyPrice: finalPrice,
      });
    };

    return (
      <Box>
        <Typography variant="subtitle1">
          Product ID: {currentItem.id}
        </Typography>
        <Typography variant="subtitle2">Style: {currentItem.style}</Typography>

        <Box sx={{ mt: 2 }}>
          <Typography variant="h6">Fees & Final Price</Typography>
          <TextField
            label="Unit Price"
            type="number"
            fullWidth
            margin="dense"
            value={currentItem.unit_price}
            onChange={(e) => handleFeeChange("unit_price", e)}
          />
          <TextField
            label="Shipping Fee"
            type="number"
            fullWidth
            margin="dense"
            value={currentItem.shippingFee}
            onChange={(e) => handleFeeChange("shippingFee", e)}
          />
          <TextField
            label="Personalization Fee"
            type="number"
            fullWidth
            margin="dense"
            value={currentItem.personalizationFee}
            onChange={(e) => handleFeeChange("personalizationFee", e)}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={currentItem.enableRebates}
                onChange={(e) =>
                  updateCurrentItem({
                    enableRebates: e.target.checked,
                    rebates: e.target.checked ? currentItem.rebates : 0,
                  })
                }
              />
            }
            label="Enable Rebates"
          />
          {currentItem.enableRebates && (
            <TextField
              label="Rebates"
              type="number"
              fullWidth
              margin="dense"
              value={currentItem.rebates}
              onChange={(e) => handleFeeChange("rebates", e)}
            />
          )}

          <TextField
            label="Wholesale Transactional Fee (6%)"
            value={currentItem.wholesaleTransactionalFee}
            InputProps={{ readOnly: true }}
            fullWidth
            margin="dense"
          />
          <TextField
            label="GST (10%)"
            value={currentItem.gst}
            InputProps={{ readOnly: true }}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Final Cost Price"
            value={currentItem.cost}
            InputProps={{ readOnly: true }}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Final Shopify Price"
            value={currentItem.finalShopifyPrice}
            InputProps={{ readOnly: true }}
            fullWidth
            margin="dense"
          />
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography variant="h6">Select Sizes</Typography>
          <FormControl fullWidth margin="dense">
            <InputLabel id="sizes-label">Sizes</InputLabel>
            <Select
              labelId="sizes-label"
              multiple
              value={currentItem.sizes || []}
              onChange={(e) => updateCurrentItem({ sizes: e.target.value })}
              label="Sizes"
            >
              {availableSizes.map((size) => (
                <MenuItem key={size.id} value={size.id}>
                  {size.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography variant="h6">Edit Description & Images</Typography>
          <TextField
            label="Description"
            fullWidth
            multiline
            rows={3}
            margin="dense"
            value={currentItem.description}
            onChange={(e) => updateCurrentItem({ description: e.target.value })}
          />
          {[1, 2, 3].map((num) => (
            <Box key={num} sx={{ mt: 2 }}>
              <Typography variant="body2">Image {num}:</Typography>
              {currentItem[`image${num}`] && (
                <img
                  src={currentItem[`image${num}`]}
                  alt={`Image${num}`}
                  style={{ width: 100, height: 100, marginRight: 8 }}
                />
              )}
              <Button variant="outlined" component="label" size="small">
                Upload
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={(e) =>
                    handleImageUpload(e.target.files[0], `image${num}`)
                  }
                />
              </Button>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  const breadcrumbs = [
    <Link key="1" to="/admin/shopify-management/">
      Shopify Management
    </Link>,
    <Typography key="2">Collection - {storeDetails?.title || ""}</Typography>,
  ];

  return (
    <>
      <Intro
        pageTitle={`Store - ${storeDetails?.name || ""}`}
        pageTitleShort="Store"
        breadcrumbs={breadcrumbs}
        additionalWidgets=""
      />
      <Container>
        {isLoading ? (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {storeDetails && (
              <Box mb={4}>
                <Typography variant="h5">{storeDetails.title}</Typography>
                <Typography variant="subtitle1">
                  Type: {storeDetails.type}
                </Typography>
              </Box>
            )}
            <Stack
              direction="row"
              justifyContent="flex-end"
              alignItems="center"
              mb={2}
            >
              <Button variant="contained" onClick={handleOpenSyncWizard}>
                Sync Products
              </Button>
            </Stack>
            <Paper>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" />
                      <TableCell>ID</TableCell>
                      <TableCell>Style</TableCell>
                      <TableCell>Price</TableCell>
                      <TableCell>Gender</TableCell>
                      <TableCell>Image</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {storeItems.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedItems.includes(item.id)}
                            onChange={() => handleSelect(item.id)}
                          />
                        </TableCell>
                        <TableCell>{item.id}</TableCell>
                        <TableCell>
                          {item.production_order?.style || "-"}
                        </TableCell>
                        <TableCell>{item.unit_price}</TableCell>
                        <TableCell>{item.gender}</TableCell>
                        <TableCell>
                          {item.image_url ? (
                            <img
                              src={item.image_url}
                              alt="Product"
                              style={{ width: 50, height: 50 }}
                            />
                          ) : (
                            "-"
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </>
        )}
      </Container>

      <Dialog
        open={syncWizardOpen}
        onClose={() => !isSubmittingSync && setSyncWizardOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Sync Selected Products — Step {activeStep + 1} of {totalSteps}
        </DialogTitle>
        <DialogContent dividers>{renderWizardContent()}</DialogContent>
        <DialogActions>
          <Button
            onClick={() => setSyncWizardOpen(false)}
            disabled={isSubmittingSync}
          >
            Cancel
          </Button>
          {activeStep > 0 && (
            <Button onClick={handleBack} disabled={isSubmittingSync}>
              Back
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={isSubmittingSync}
            startIcon={isSubmittingSync && <CircularProgress size={20} />}
          >
            {activeStep === totalSteps - 1 ? "Finish" : "Next"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ShopifySingleView;
